/**
 * Voting Stats Service Layer
 * Handles employee of the month statistics, streaks, and leaderboard
 *
 * Collection: voting_stats/{userId}
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  arrayUnion,
  query,
  where,
  orderBy,
  limit,
} from 'firebase/firestore';
import { db } from '../firebase';
import { VotingStats, VotingResults } from '../types/schema';
import { stripUndefinedDeep } from '../utils/firestore';
import { getTenantIdSync, tenantIdReadUnion } from '../hooks/useTenantId';

const VOTING_STATS_COLLECTION = 'voting_stats';

// ============================================================================
// STATS UPDATE (called after voting period closes)
// ============================================================================

/**
 * Update voting stats for all nominees after a voting period closes
 * Called by calculateVotingResults()
 */
export async function updateVotingStatsForPeriod(
  votingPeriodId: string,
  results: VotingResults
): Promise<void> {
  const updates = results.results.map(async (result) => {
    await updateEmployeeVotingStats(
      result.userId,
      result.userName,
      result.userRole,
      {
        periodId: votingPeriodId,
        month: results.month,
        year: results.year,
        votesReceived: result.voteCount,
        totalVotes: results.totalVotes,
        percentage: result.percentage,
        rank: result.rank,
        isWinner: result.rank === 1,
      },
      results.tenantId
    );
  });

  await Promise.all(updates);
}

/**
 * Update individual employee voting stats
 */
async function updateEmployeeVotingStats(
  userId: string,
  userName: string,
  userRole: string | undefined,
  periodResult: {
    periodId: string;
    month: string;
    year: number;
    votesReceived: number;
    totalVotes: number;
    percentage: number;
    rank: number;
    isWinner: boolean;
  },
  tenantId?: string
): Promise<void> {
  // Always resolve a canonical tenantId so every stats doc is isolation-ready
  // (the create + update branches below stamp `tid`).
  const tid = tenantId ?? getTenantIdSync();
  const statsRef = doc(db, VOTING_STATS_COLLECTION, userId);
  const statsSnap = await getDoc(statsRef);

  const now = new Date().toISOString();

  if (!statsSnap.exists()) {
    // Create new stats document.
    // NOTE: userRole / lastWinDate / lastWinPeriodId / firstWinAt all land as
    // `undefined` for a non-winner with no role. Firestore REJECTS any document
    // carrying `undefined` ("Unsupported field value: undefined") — this was the
    // crash that failed the recovery sweep's stats backfill. stripUndefinedDeep
    // (applied on the write below) drops them; tenantId is still gated inline.
    const newStats: Omit<VotingStats, 'id'> = {
      userId,
      userName,
      userRole,
      tenantId: tid,
      totalWins: periodResult.isWinner ? 1 : 0,
      totalNominations: 1,
      totalVotes: periodResult.votesReceived,
      currentStreak: periodResult.isWinner ? 1 : 0,
      longestStreak: periodResult.isWinner ? 1 : 0,
      lastWinDate: periodResult.isWinner ? periodResult.month : undefined,
      lastWinPeriodId: periodResult.isWinner ? periodResult.periodId : undefined,
      wins: periodResult.isWinner
        ? [
            {
              periodId: periodResult.periodId,
              month: periodResult.month,
              year: periodResult.year,
              votesReceived: periodResult.votesReceived,
              totalVotes: periodResult.totalVotes,
              percentage: periodResult.percentage,
              rank: periodResult.rank,
              wonAt: now,
            },
          ]
        : [],
      firstWinAt: periodResult.isWinner ? now : undefined,
      processedPeriodIds: [periodResult.periodId],
      lastUpdated: now,
    };

    await setDoc(statsRef, stripUndefinedDeep(newStats));
  } else {
    // Update existing stats
    const currentStats = statsSnap.data() as VotingStats;

    // Idempotency guard: if this period has already been folded into the
    // totals, do nothing. Protects against double-counting when stats are
    // applied more than once (cron retry, client recovery sweep, or both
    // the server cron AND the client closing the same period).
    if (currentStats.processedPeriodIds?.includes(periodResult.periodId)) {
      return;
    }

    const updatedWins = periodResult.isWinner
      ? [
          ...currentStats.wins,
          {
            periodId: periodResult.periodId,
            month: periodResult.month,
            year: periodResult.year,
            votesReceived: periodResult.votesReceived,
            totalVotes: periodResult.totalVotes,
            percentage: periodResult.percentage,
            rank: periodResult.rank,
            wonAt: now,
          },
        ]
      : currentStats.wins;

    // Is this period OLDER than the user's most recent recorded win? This
    // happens when the recovery sweep backfills a long-stuck OLD period AFTER a
    // newer period has already been folded in (e.g. May closes weeks late, in
    // June, after June already applied). Cumulative totals (wins, totalWins,
    // totalNominations, totalVotes) are still correct to add, but the "current"
    // snapshot fields — currentStreak / lastWinDate — must NOT be moved
    // backwards by a stale period, or the genuinely-current winner's streak gets
    // clobbered to 0.
    const isOlderThanLastWin =
      !!currentStats.lastWinDate && periodResult.month < currentStats.lastWinDate;

    // Calculate streak
    let currentStreak = currentStats.currentStreak || 0;
    if (periodResult.isWinner) {
      if (isOlderThanLastWin) {
        // Backfilling an older win — leave the current streak as-is.
      } else {
        // Check if this is consecutive month
        const lastWinMonth = currentStats.lastWinDate;
        if (lastWinMonth) {
          const lastDate = new Date(lastWinMonth + '-01');
          const thisDate = new Date(periodResult.month + '-01');
          const monthsDiff =
            (thisDate.getFullYear() - lastDate.getFullYear()) * 12 +
            (thisDate.getMonth() - lastDate.getMonth());

          if (monthsDiff === 1) {
            currentStreak++;
          } else {
            currentStreak = 1;
          }
        } else {
          currentStreak = 1;
        }
      }
    } else if (!isOlderThanLastWin) {
      // Only a CURRENT/NEWER non-win breaks the streak. A stale backfilled
      // non-win period must not reset a streak established by a later win.
      currentStreak = 0;
    }

    const longestStreak = Math.max(currentStats.longestStreak || 0, currentStreak);

    // stripUndefinedDeep: `userRole` is optional and lands as `undefined` for a
    // roleless nominee — Firestore rejects undefined. arrayUnion + the spread
    // sentinels are preserved (non-plain-object prototypes pass through).
    await updateDoc(
      statsRef,
      stripUndefinedDeep({
        userName, // Update in case name changed
        userRole,
        // Backfill tenantId on legacy docs that pre-date the field — only
        // write if the stored value is missing/empty so we don't clobber
        // a later canonical value with stale data from the calling period.
        ...(!currentStats.tenantId ? { tenantId: tid } : {}),
        totalWins: (currentStats.totalWins || 0) + (periodResult.isWinner ? 1 : 0),
        totalNominations: (currentStats.totalNominations || 0) + 1,
        totalVotes: (currentStats.totalVotes || 0) + periodResult.votesReceived,
        currentStreak,
        longestStreak,
        // Only advance lastWin* for a win that's newer than what's recorded —
        // never let a stale backfilled win move the pointer backwards.
        ...(periodResult.isWinner && !isOlderThanLastWin && {
          lastWinDate: periodResult.month,
          lastWinPeriodId: periodResult.periodId,
        }),
        wins: updatedWins,
        ...(!currentStats.firstWinAt &&
          periodResult.isWinner && {
            firstWinAt: now,
          }),
        processedPeriodIds: arrayUnion(periodResult.periodId),
        lastUpdated: now,
      })
    );
  }
}

// ============================================================================
// LEADERBOARD QUERIES
// ============================================================================

// NOTE on tenant filtering:
// `voting_stats` documents written before V107.52.34 had no tenantId field.
// The leaderboard reads below now ALWAYS tenant-filter (via tenantIdReadUnion)
// so they stay valid under a future strict isolation rule. This is safe ONLY
// after `npm run backfill:tenant-coverage` stamps tenantId onto the legacy
// docs — until then, pre-tenancy stats drop out of the leaderboard. The writer
// (updateEmployeeVotingStats) now always stamps the canonical tenantId, so
// new docs are covered. Each two-orderBy query needs a composite index
// (tenantId, <sortA> desc, <sortB> desc) — see firestore.indexes.json.

/**
 * Get top employees by total wins
 */
export async function getTopEmployeesByWins(
  tenantId?: string,
  limitCount: number = 10
): Promise<VotingStats[]> {
  const q = query(
    collection(db, VOTING_STATS_COLLECTION),
    where('tenantId', 'in', tenantIdReadUnion(tenantId ?? getTenantIdSync())),
    orderBy('totalWins', 'desc'),
    orderBy('totalVotes', 'desc'),
    limit(limitCount)
  );

  const snapshot = await getDocs(q);

  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as VotingStats[];
}

/**
 * Get employees with longest current streak
 */
export async function getTopEmployeesByStreak(
  tenantId?: string,
  limitCount: number = 10
): Promise<VotingStats[]> {
  const q = query(
    collection(db, VOTING_STATS_COLLECTION),
    where('tenantId', 'in', tenantIdReadUnion(tenantId ?? getTenantIdSync())),
    orderBy('currentStreak', 'desc'),
    orderBy('totalWins', 'desc'),
    limit(limitCount)
  );

  const snapshot = await getDocs(q);

  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as VotingStats[];
}

/**
 * Get employee stats by user ID
 */
export async function getEmployeeVotingStats(userId: string): Promise<VotingStats | null> {
  const docRef = doc(db, VOTING_STATS_COLLECTION, userId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  return {
    id: docSnap.id,
    ...docSnap.data(),
  } as VotingStats;
}

/**
 * Get all employee stats (admin function).
 * See "Never-tenant-filter pattern" note above — tenantId param retained
 * for API compatibility but the filter is dropped to surface legacy docs.
 */
export async function getAllEmployeeStats(tenantId?: string): Promise<VotingStats[]> {
  const q = query(
    collection(db, VOTING_STATS_COLLECTION),
    where('tenantId', 'in', tenantIdReadUnion(tenantId ?? getTenantIdSync())),
    orderBy('totalWins', 'desc'),
    orderBy('totalVotes', 'desc')
  );

  const snapshot = await getDocs(q);

  return snapshot.docs.map((doc, index) => ({
    id: doc.id,
    ...doc.data(),
    allTimeRank: index + 1,
  })) as VotingStats[];
}

/**
 * Get employees with longest all-time streak
 */
export async function getTopEmployeesByLongestStreak(
  tenantId?: string,
  limitCount: number = 10
): Promise<VotingStats[]> {
  const q = query(
    collection(db, VOTING_STATS_COLLECTION),
    where('tenantId', 'in', tenantIdReadUnion(tenantId ?? getTenantIdSync())),
    orderBy('longestStreak', 'desc'),
    orderBy('totalWins', 'desc'),
    limit(limitCount)
  );

  const snapshot = await getDocs(q);

  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as VotingStats[];
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get hall of fame stats (most wins, longest streak, etc.)
 */
export async function getHallOfFameStats(tenantId?: string) {
  const allStats = await getAllEmployeeStats(tenantId);

  if (allStats.length === 0) {
    return {
      mostWins: null,
      longestStreak: null,
      longestCurrentStreak: null,
      mostVotesAllTime: null,
    };
  }

  const mostWins = allStats.reduce((max, stat) =>
    stat.totalWins > max.totalWins ? stat : max
  );

  const longestStreak = allStats.reduce((max, stat) =>
    (stat.longestStreak || 0) > (max.longestStreak || 0) ? stat : max
  );

  const longestCurrentStreak = allStats.reduce((max, stat) =>
    (stat.currentStreak || 0) > (max.currentStreak || 0) ? stat : max
  );

  const mostVotesAllTime = allStats.reduce((max, stat) =>
    stat.totalVotes > max.totalVotes ? stat : max
  );

  return {
    mostWins,
    longestStreak,
    longestCurrentStreak,
    mostVotesAllTime,
  };
}
